import { Component, Input, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { User } from '@shared/models/user.model';
import { UserService } from '@shared/services/user.service';
import { FriendsService } from '@app/home/friends/friends.service';
import { Friend, Friendship } from '@app/home/friends/friends.model';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';

import { takeUntil, first } from 'rxjs/operators';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { Observable } from 'rxjs';
import { AvatarComponent } from '@app/shared/components/avatar/avatar.component';
import { Heading2Component } from '@app/shared/components/text/h2/h2.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { DividerComponent } from '@app/shared/components/divider/divider.component';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-friends-item',
  templateUrl: './friends-item.component.html',
  styleUrls: ['./friends-item.component.scss'],
  standalone: true,
  imports: [AvatarComponent, Heading2Component, ButtonComponent, DividerComponent],
})
export class FriendsItemComponent implements OnInit {
  @Input() friend: Friend;

  isSelf: boolean;
  currentUser: User;
  componentDestroy: () => Observable<unknown>;

  private readonly userService = inject(UserService);
  private readonly friendsService = inject(FriendsService);
  private readonly dialog = inject(MatDialog);
  public readonly logger = inject(LoggerService);
  private readonly eventManager = inject(EventManager);

  ngOnInit() {
    this.userService.currentUser$.pipe(takeUntil(this.componentDestroy()), first()).subscribe({
      next: (user: User) => {
        this.currentUser = user;
        this.isSelf = user.id == this.friend.id;
      },
      error: (error) => this.logger.error(error),
    });
  }

  acceptFriendRequest() {
    this.friendsService
      .acceptFriendRequest(this.friend.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (friend: Friend) => {
          this.friendsService.notifyAcceptFriend(friend);
          this.friend.friendship = Friendship.Friend;
        },
        error: (error) => this.logger.error(error),
      });
  }

  rejectFriendRequest() {
    this.friendsService
      .rejectFriendRequest(this.friend.id)
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (userId: string) => {
          this.friendsService.notifyRejectFriend(userId);
          this.friend.friendship = Friendship.Unfriend;
        },
        error: (error) => this.logger.error(error),
      });
  }

  publicProfile() {
    if (!this.isSelf) {
      if (!this.dialog.getDialogById(NonPersistedModalName.PublicProfileComponent)) {
        this.eventManager.openModal(
          new Modal(
            NonPersistedModalName.PublicProfileComponent,
            ModalType.View,
            { userId: this.friend.id },
            'big-dialog'
          )
        );
      } else {
        this.eventManager.closeModal(new Modal(NonPersistedModalName.PublicProfileComponent, ModalType.View));
        this.dialog
          .getDialogById(NonPersistedModalName.PublicProfileComponent)
          .afterClosed()
          .subscribe(() => {
            this.eventManager.openModal(
              new Modal(
                NonPersistedModalName.PublicProfileComponent,
                ModalType.View,
                { userId: this.friend.id },
                'big-dialog'
              )
            );
          });
      }
    }
  }
}
